<script lang="ts">
import type { AcceptableValue } from 'reka-ui'
import type { Component, InputHTMLAttributes } from 'vue'
import type { UseComponentIconsProps } from '../composables/useComponentIcons'
import { buttonGroupVariantWithRootUi } from './AwButtonGroup.vue'

export interface InputProps extends UseComponentIconsProps {
  as?: string | Component
  id?: string
  name?: string
  size?: 'sm' | 'md' | 'lg'
  type?: InputHTMLAttributes['type']
  placeholder?: string
  color?: 'default' | 'critical' | 'warning'
  required?: boolean
  autocomplete?: InputHTMLAttributes['autocomplete']
  autofocus?: boolean
  autofocusDelay?: number
  disabled?: boolean
  isBorderless?: boolean
  debounceDelay?: number
  modelModifiers?: {
    string?: boolean
    number?: boolean
    trim?: boolean
    lazy?: boolean
    nullify?: boolean
  }
  class?: string | object
}

export interface InputEmits<T extends AcceptableValue = AcceptableValue> {
  (e: 'update:modelValue', payload: T): void
  (e: 'blur', event: FocusEvent): void
  (e: 'change', event: Event): void
  (e: 'focus', event: FocusEvent): void
}

export interface InputSlots {
  leading?: (props?: object) => any
  trailing?: (props?: object) => any
}

export const ui = {
  root: 'relative inline-flex items-center',
  base: 'w-full rounded-md placeholder:text-secondary-700 focus:outline-none transition-colors',
  leading: 'absolute inset-y-0 start-0 flex items-center',
  leadingIcon: 'shrink-0 text-secondary-600',
  trailing: 'absolute inset-y-0 end-0 flex items-center',
  trailingIcon: 'shrink-0 text-secondary-600',
  size: {
    sm: {
      base: 'px-2.5 py-1.5 text-md',
      leading: 'ps-3',
      trailing: 'pe-3',
      leadingIcon: 'size-3',
      trailingIcon: 'size-3',
    },
    md: {
      base: 'px-3 py-2 text-md',
      leading: 'ps-3',
      trailing: 'pe-3',
      leadingIcon: 'size-3',
      trailingIcon: 'size-3',
    },
    lg: {
      base: 'px-4 py-3 text-md',
      leading: 'ps-3',
      trailing: 'pe-3',
      leadingIcon: 'size-4',
      trailingIcon: 'size-4',
    },
  },
  color: {
    default: {
      base: 'bg-white disabled:cursor-not-allowed disabled:text-gray-600 disabled:bg-gray-100',
      border: 'border border-secondary-400 not-disabled:hover:border-secondary-700 focus:border-secondary-800! focus:border-secondary-800 ring-1 ring-transparent focus:ring-secondary-200',
    },
    critical: {
      base: 'disabled:cursor-not-allowed bg-critical-50',
      border: 'border border-critical-500 not-disabled:hover:border-critical-600 focus:border-critical-600! ring-1 ring-transparent focus:ring-critical-200',
    },
    warning: {
      base: 'disabled:cursor-not-allowed bg-warning-50',
      border: 'border border-warning-500 not-disabled:hover:border-warning-600 focus:border-warning-600! ring-1 ring-transparent focus:ring-warning-200',
    },
  },
  ...buttonGroupVariantWithRootUi,
}
</script>

<script setup lang="ts" generic="T extends AcceptableValue">
import { Primitive } from 'reka-ui'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useButtonGroup } from '../composables/useButtonGroup'
import { useComponentIcons } from '../composables/useComponentIcons'
import { looseToNumber } from '../utils'
import AwIcon from './AwIcon.vue'

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<InputProps>(), {
  type: 'text',
  autocomplete: 'off',
  autofocusDelay: 0,
  debounceDelay: undefined,
})
const emits = defineEmits<InputEmits<T>>()
const slots = defineSlots<InputSlots>()

const [modelValue, modelModifiers] = defineModel<T>()

const { hasButtonGroup, orientation, size: buttonGroupSize } = useButtonGroup<InputProps>(props)
const { isLeading, isTrailing, leadingIconName, trailingIconName } = useComponentIcons(props, slots)

const inputRef = ref<HTMLInputElement | null>(null)
let debounceTimer: ReturnType<typeof setTimeout> | null = null

function updateInput(value: string | null) {
  if (modelModifiers.trim)
    value = value?.trim() ?? null
  if (modelModifiers.number || props.type === 'number')
    value = looseToNumber(value)
  if (modelModifiers.nullify)
    value ||= null
  modelValue.value = value as T
}
function onInput(event: Event) {
  if (!modelModifiers.lazy) {
    const value = (event.target as HTMLInputElement).value
    if (props.debounceDelay !== undefined) {
      // Debounce mode
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }
      debounceTimer = setTimeout(() => {
        updateInput(value)
        debounceTimer = null
      }, props.debounceDelay)
    }
    else {
      // Normal mode
      updateInput(value)
    }
  }
}
function onChange(event: Event) {
  const value = (event.target as HTMLInputElement).value
  if (modelModifiers.lazy)
    updateInput(value)
  if (modelModifiers.trim)
    (event.target as HTMLInputElement).value = value.trim()
  emits('change', event)
}
function onBlur(event: FocusEvent) {
  emits('blur', event)
}
function onFocus(event: FocusEvent) {
  emits('focus', event)
}
function autoFocus() {
  if (props.autofocus) {
    inputRef.value?.focus()
  }
}

const size = computed(() => props.size ?? 'md')
const color = computed(() => props.color ?? 'default')
const rootClass = computed(() => [
  ui.root,
  props.class,
  hasButtonGroup.value && ui.buttonGroup[orientation.value].root,
])
const baseClass = computed(() => [
  ui.base,
  ui.color[color.value].base,
  !props.isBorderless && ui.color[color.value].border,
  ui.size[buttonGroupSize.value ?? size.value].base,
  hasButtonGroup.value && ui.buttonGroup[orientation.value].base,
  isTrailing.value && 'pr-7',
  isLeading.value && 'pl-7',
])
const trailingClass = computed(() => [
  ui.trailing,
  ui.size[buttonGroupSize.value ?? size.value].trailing,
])
const trailingIconClass = computed(() => [
  ui.trailingIcon,
  ui.size[buttonGroupSize.value ?? size.value].trailingIcon,
])
const leadingClass = computed(() => [
  ui.leading,
  ui.size[buttonGroupSize.value ?? size.value].leading,
  props.isLoading && !isLeading.value && 'animate-spin',
])
const leadingIconClass = computed(() => [
  ui.leadingIcon,
  ui.size[buttonGroupSize.value ?? size.value].leadingIcon,
  props.isLoading && 'animate-spin',
])

onMounted(() => {
  setTimeout(() => {
    autoFocus()
  }, props.autofocusDelay)
})

onBeforeUnmount(() => {
  if (debounceTimer) {
    clearTimeout(debounceTimer)
  }
})

defineExpose({ inputRef })
</script>

<template>
  <Primitive :as="as" :class="rootClass">
    <input
      :id ref="inputRef" :type :value="modelValue" :name :placeholder :class="baseClass" :disabled :required
      :autocomplete v-bind="{ ...$attrs }" @input="onInput" @blur="onBlur" @change="onChange" @focus="onFocus"
    >
    <span v-if="isLeading || !!slots.leading" :class="leadingClass">
      <slot name="leading">
        <AwIcon v-if="isLeading && leadingIconName" :name="leadingIconName" :class="leadingIconClass" />
      </slot>
    </span>
    <span v-if="isTrailing || !!slots.trailing" :class="trailingClass">
      <slot name="trailing">
        <AwIcon v-if="trailingIconName" :name="trailingIconName" :class="trailingIconClass" />
      </slot>
    </span>
  </Primitive>
</template>
